import {
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { FaTrash } from "react-icons/fa";
import {
  EntraGroupDto,
  OrganizationRoleType,
} from "../../../../autogen/bff-api";
import { EntraMappingModal } from "./EntraMappingModal";
import { useRoleName } from "./useRoleName";

interface Props {
  groups: EntraGroupDto[];
  departments: { id: string; name: string }[];
  isBusy: boolean;
  onSetRole: (
    group: EntraGroupDto,
    role: OrganizationRoleType,
    departmentId: string | null,
  ) => Promise<boolean>;
  onRemove: (mappingId: string) => void;
}

export const EntraGroupTable = ({
  groups,
  departments,
  isBusy,
  onSetRole,
  onRemove,
}: Props) => {
  const roleName = useRoleName();
  const [editing, setEditing] = useState<EntraGroupDto | null>(null);

  const mapped = groups.filter((group) => group.mapping);
  const unmapped = groups.filter((group) => !group.mapping);

  if (groups.length === 0) {
    return (
      <Box px="5" py="6">
        <Text fontSize="sm" color="smMuted">
          {t("Entra ID has not provisioned any groups for this tenant yet")}.
        </Text>
      </Box>
    );
  }

  const memberCount = (group: EntraGroupDto) => (
    <Td isNumeric borderColor="smBorderSubtle" width="1">
      <Text
        fontSize="sm"
        color={group.memberCount === 0 ? "smTextDisabled" : "smText"}
      >
        {group.memberCount}
      </Text>
    </Td>
  );

  const listCard = (title: string, hint: string, body: React.ReactNode) => (
    <Box
      border="1px solid"
      borderColor="smBorder"
      borderRadius="md"
      backgroundColor="smBackground"
      overflow="hidden"
    >
      <Box px="4" py="3" borderBottom="1px solid" borderColor="smBorderSubtle">
        <Text fontWeight="semibold" fontSize="sm">
          {title}
        </Text>
        <Text fontSize="xs" color="smMuted">
          {hint}
        </Text>
      </Box>
      <Box px="4" py="2">
        {body}
      </Box>
    </Box>
  );

  return (
    <Flex direction="column" gap="4" px="5" py="5">
      {listCard(
        t("Groups that give access"),
        t("Everyone Entra ID keeps in one of these groups gets its role"),
        mapped.length === 0 ? (
          <Text fontSize="sm" color="smMuted" py="2">
            {t("No group gives access yet")}.
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th pl="0" borderColor="smBorderSubtle">
                    {t("Group")}
                  </Th>
                  <Th isNumeric borderColor="smBorderSubtle">
                    {t("Members")}
                  </Th>
                  <Th borderColor="smBorderSubtle">{t("Role")}</Th>
                  <Th borderColor="smBorderSubtle">{t("Applies to")}</Th>
                  <Th pr="0" borderColor="smBorderSubtle" />
                </Tr>
              </Thead>
              <Tbody>
                {mapped.map((group) => {
                  const mapping = group.mapping;
                  if (!mapping) return null;
                  const department = departments.find(
                    (candidate) => candidate.id === mapping.departmentId,
                  );

                  return (
                    <Tr key={group.userGroupId}>
                      <Td pl="0" borderColor="smBorderSubtle">
                        <Text fontWeight="medium">{group.displayName}</Text>
                      </Td>
                      {memberCount(group)}
                      <Td borderColor="smBorderSubtle">
                        <Text fontSize="sm">{roleName(mapping.role)}</Text>
                      </Td>
                      <Td borderColor="smBorderSubtle">
                        <Text fontSize="sm">
                          {department?.name ?? t("The whole organization")}
                        </Text>
                      </Td>
                      <Td pr="0" borderColor="smBorderSubtle">
                        <Flex justifyContent="flex-end" gap="1">
                          <Button
                            size="xs"
                            variant="ghost"
                            isDisabled={isBusy}
                            onClick={() => setEditing(group)}
                          >
                            {t("Change")}
                          </Button>
                          <IconButton
                            aria-label={t("Remove the group mapping")}
                            icon={<FaTrash />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            isDisabled={isBusy}
                            onClick={() => onRemove(mapping.id)}
                          />
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        ),
      )}

      {unmapped.length > 0 &&
        listCard(
          t("Groups without a role"),
          t("Entra ID keeps these in sync, but they give no access here"),
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th pl="0" borderColor="smBorderSubtle">
                    {t("Group")}
                  </Th>
                  <Th isNumeric borderColor="smBorderSubtle">
                    {t("Members")}
                  </Th>
                  <Th pr="0" borderColor="smBorderSubtle" />
                </Tr>
              </Thead>
              <Tbody>
                {unmapped.map((group) => (
                  <Tr key={group.userGroupId}>
                    <Td pl="0" borderColor="smBorderSubtle">
                      <Text color="smMuted">{group.displayName}</Text>
                    </Td>
                    {memberCount(group)}
                    <Td pr="0" borderColor="smBorderSubtle">
                      <Flex justifyContent="flex-end">
                        <Button
                          size="xs"
                          variant="outline"
                          colorScheme="teal"
                          isDisabled={isBusy}
                          onClick={() => setEditing(group)}
                        >
                          {t("Give access")}
                        </Button>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>,
        )}

      {editing && (
        <EntraMappingModal
          group={editing}
          departments={departments}
          isBusy={isBusy}
          onSave={async (role, departmentId) => {
            if (await onSetRole(editing, role, departmentId)) setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </Flex>
  );
};
